import { forwardRef } from 'react';
import type { HTMLAttributes } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Les tons de la pastille. Ce sont EXACTEMENT ceux du contrat des vues
 * (`Tone`, packages/ui/src/views/contract.ts) : le meme statut doit rendre la
 * meme couleur en liste, en fiche, en tableau, en kanban et en calendrier.
 * Aucune couleur nouvelle : chaque ton pointe un token existant.
 */
export type StatusBadgeTone = 'success' | 'neutral' | 'accent' | 'danger';

export interface StatusBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /** Texte du statut (ex: "3 installées", "En ligne"). */
  label: string;
  /** Couleur du point d'état. `success` (vert) par défaut. */
  tone?: StatusBadgeTone;
}

/** Couleur du point selon le ton. Le label reste discret (ink-500). */
const DOT_TONE: Record<StatusBadgeTone, string> = {
  success: 'bg-success',
  neutral: 'bg-ink-400',
  accent: 'bg-accent-dot',
  danger: 'bg-danger',
};

/**
 * Pastille de statut : point coloré + label discret.
 * Réf. brand book « Badges, statuts & divers » : h-8, border, surface-1,
 * point 7px (defaut --success), label 12px medium ink.
 */
export const StatusBadge = forwardRef<HTMLSpanElement, StatusBadgeProps>(function StatusBadge(
  { label, tone = 'success', className, ...props },
  ref,
) {
  return (
    <span
      ref={ref}
      className={cn(
        'inline-flex h-8 items-center gap-2.5 rounded-[0.5625rem] border border-border bg-surface-1 px-3',
        className,
      )}
      {...props}
    >
      <span className={cn('h-[0.4375rem] w-[0.4375rem] shrink-0 rounded-full', DOT_TONE[tone])} />
      <span className="text-xs font-medium text-ink-500">{label}</span>
    </span>
  );
});
